import type { ReactNode } from 'react'
import { ChevronDown, Search, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { FILTER_FIELD_SHELL_CLASS, FILTER_HOVER_BG_ACCENT_60, FILTER_HOVER_TEXT_FOREGROUND, FILTER_POPOVER_CONTENT_CLASS } from '@/components/filters/filter-styles'
import { FilterPopoverPanel } from '@/components/filters/filter-dropdown-shell'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

/** Inline section label (e.g. “Jobs”) — one shared lead for both browse bars. */
export function ListBrowseSectionTitle({ title }: { title: string }) {
  return (
    <div className="mr-1 flex min-w-0 shrink-0 items-baseline gap-1.5">
      <span className="text-sm font-semibold tracking-tight text-foreground">{title}</span>
    </div>
  )
}

export type ListBrowseToolbarSearchProps = {
  searchId?: string
  searchPlaceholder: string
  searchValue: string
  onSearchChange: (value: string) => void
  onSearchBlur?: (value: string) => void
  /** Inline section label (e.g. “Jobs”), aligned with search as one toolbar row. */
  sectionTitle?: string
  className?: string
}

export type ListBrowseLeadingScopeDensity = 'toolbar' | 'unified'

export type ListBrowseLeadingScopeProps = {
  label: string
  displayValue: string
  popoverContent: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  density?: ListBrowseLeadingScopeDensity
}

const LEADING_SCOPE_DENSITY_CLASS: Record<ListBrowseLeadingScopeDensity, string | undefined> = {
  toolbar: undefined,
  unified: '-my-[3px] h-auto self-stretch',
}

export function ListBrowseLeadingScope({
  label,
  displayValue,
  popoverContent,
  open,
  onOpenChange,
  disabled,
  density = 'toolbar',
}: ListBrowseLeadingScopeProps) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-leading-scope
          disabled={disabled}
          className={cn(
            'flex h-8 max-w-[10rem] shrink-0 items-center gap-1.5 rounded-l-md border-r border-border/50 px-3 text-sm font-medium text-foreground transition-[color,background-color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
            FILTER_HOVER_BG_ACCENT_60,
            'active:scale-[0.98] motion-reduce:active:scale-100 motion-reduce:transition-none',
            LEADING_SCOPE_DENSITY_CLASS[density],
          )}
          aria-label={`${label}: ${displayValue}`}
        >
          <span className="truncate">{displayValue}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          'w-auto min-w-[14rem] max-w-[min(100vw-2rem,20rem)] p-0',
          FILTER_POPOVER_CONTENT_CLASS,
        )}
      >
        <FilterPopoverPanel>{popoverContent}</FilterPopoverPanel>
      </PopoverContent>
    </Popover>
  )
}

export type ListBrowseToolbarProps = ListBrowseToolbarSearchProps & {
  leadingScope?: ListBrowseLeadingScopeProps
  /** View switcher beside search (list/graph, cards/table, …). */
  viewSwitcher?: ReactNode
  /** Filter funnel menu (`SecondaryFiltersMenu`). */
  filterMenu?: ReactNode
  /** Sort and other trailing icon controls. */
  trailingActions?: ReactNode
}

export function ListBrowseToolbar({
  searchId = 'list-filter-search',
  searchPlaceholder,
  searchValue,
  onSearchChange,
  onSearchBlur,
  sectionTitle,
  className,
  leadingScope,
  viewSwitcher,
  filterMenu,
  trailingActions,
}: ListBrowseToolbarProps) {
  const showLead = Boolean(sectionTitle)
  const showControls = Boolean(viewSwitcher || filterMenu || trailingActions)

  return (
    <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-1', className)}>
      {showLead && sectionTitle ? <ListBrowseSectionTitle title={sectionTitle} /> : null}
      <div className={cn('flex items-center overflow-hidden', FILTER_FIELD_SHELL_CLASS)}>
        {leadingScope ? <ListBrowseLeadingScope {...leadingScope} /> : null}
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id={searchId}
            type="search"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onBlur={onSearchBlur ? (e) => onSearchBlur(e.target.value) : undefined}
            onKeyDown={
              onSearchBlur
                ? (e) => {
                    if (e.key === 'Enter') onSearchBlur(e.currentTarget.value)
                  }
                : undefined
            }
            className={cn(
              'h-8 border-0 bg-transparent pl-8 text-sm shadow-none placeholder:italic focus-visible:ring-0',
              searchValue.trim() ? 'pr-8' : undefined,
            )}
            autoComplete="off"
          />
          {searchValue.trim() ? (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-[color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none',
                FILTER_HOVER_TEXT_FOREGROUND,
                'active:scale-[0.97] motion-reduce:active:scale-100',
              )}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>
      {showControls ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
          {viewSwitcher}
          {filterMenu}
          {trailingActions}
        </div>
      ) : null}
    </div>
  )
}
